import React, { FC, memo, ReactElement, useEffect, useState } from 'react';

import Mustache from 'mustache';

import Flex from '@vromo/ui/components/Flex';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { TextArea } from '@vromo/ui/components/Input';
import Tooltip from '@vromo/ui/components/Tooltip';
import { useTranslation } from 'react-i18next';
import {
    letterCountWarningIconStyle,
    Pane,
    PaneWrapper,
    Preview,
    PreviewComponent,
    PreviewPane,
    ShortcutItem,
    ShortcutList,
    templateStyles as styles
} from './SMSEditor.style';
import { MAX_SMS_CHARACTER_COUNT, templateData } from './SMSEditorHelper';

Mustache.escape = (value: string) => value;

const formatTemplate = (value: string) => {
    return value.replace(/{{link}}([^\s])/, (...args) => `{{link}} ${args[1]}`);
};

const RenderPreview = ({ value }: { value: string }): ReactElement => {
    try {
        return (
            <>
                {Mustache.parse(value).map((element) => {
                    if (element[0] === 'name')
                        return (
                            <PreviewComponent key={element.join('_')}>
                                {templateData[element[1]]!.mock}
                            </PreviewComponent>
                        );
                    return element[1];
                })}
            </>
        );
    } catch {
        return <>{value}</>;
    }
};

interface Props {
    value?: string;
    onChange: (value: string) => void;
}

const SMSTemplateEditor: FC<Props> = ({ value = '', onChange }) => {
    const { t } = useTranslation();

    const [letterCount, setLetterCount] = useState(`0/${MAX_SMS_CHARACTER_COUNT}`);
    const [cursorIndex, setCursorIndex] = useState(0);

    useEffect(() => {
        let count = 0;
        try {
            Mustache.parse(value).map((element) => {
                if (element[0] === 'name') {
                    count += templateData[element[1]]!.mock?.length || 0;
                } else {
                    count += element[1].length;
                }
            });
        } catch {
            count = value.length;
        }
        setLetterCount(`${count}/${MAX_SMS_CHARACTER_COUNT}`);
    }, [value]);

    const insertShortcut = (shortcut: string) => {
        const template = `{{${shortcut}}}`;
        if ((value + template).length <= MAX_SMS_CHARACTER_COUNT) {
            onChange([value.slice(0, cursorIndex), template, value.slice(cursorIndex)].join(''));
            setCursorIndex(cursorIndex + template.length);
        }
    };

    return (
        <>
            <PaneWrapper>
                <Pane>
                    <label>{t('campaign.sms')}</label>
                    <Flex style={styles.shortcutContainer} horizontal="left">
                        <strong>{t('campaign.insert')}</strong>
                        <ShortcutList>
                            {Object.keys(templateData).map((key) => {
                                const shortcutName = templateData[key]!.name;
                                return (
                                    <ShortcutItem key={shortcutName}>
                                        <a onClick={() => insertShortcut(key)}>{shortcutName}</a>
                                    </ShortcutItem>
                                );
                            })}
                        </ShortcutList>
                    </Flex>
                    <TextArea
                        style={styles.textArea}
                        value={value}
                        maxLength={MAX_SMS_CHARACTER_COUNT}
                        onChange={(e) => {
                            setCursorIndex(e.target.selectionStart);
                            onChange(formatTemplate(e.target.value));
                        }}
                    />
                </Pane>
                <PreviewPane>
                    <label>{t('campaign.preview')}</label>
                    <Preview>
                        <RenderPreview {...{ value }} />
                    </Preview>
                    <Flex style={styles.letterCountContainer} direction={'row'}>
                        <FontAwesome
                            className={letterCountWarningIconStyle}
                            icon="exclamation-triangle"
                        />
                        <small style={styles.letterCountInfo}>
                            {t('campaign.letterCountInfo')}
                        </small>
                        <small style={styles.letterCount}>{letterCount}</small>
                        <Tooltip position="bottom" label={t('campaign.letterCountTooltip')}>
                            <div>
                                <FontAwesome icon="info-circle" />
                            </div>
                        </Tooltip>
                    </Flex>
                </PreviewPane>
            </PaneWrapper>
        </>
    );
};

export default memo(SMSTemplateEditor);
